import Vue from "vue";
import Vuex from "vuex";
import persistence from "./persistence";
import socket from "./socket";
import storyLogPlugin from "./storyLogPlugin";
import players from "./modules/players";
import session from "./modules/session";
import storyLog from "./modules/storyLog";
import privateChat from "./modules/privateChat";
import room from "./modules/room";
import voice from "./modules/voice";
import roleDraw from "./modules/roleDraw";
import editionJSON from "../editions.json";
import rolesJSON from "../roles.json";
import fabledJSON from "../fabled.json";
import jinxesJSON from "../hatred.json";

const { translateOfficialReminders } = require("../utils/reminderTranslations");

Vue.use(Vuex);

// helper functions
const getRolesByEdition = (edition = editionJSON[0]) => {
  return new Map(
    rolesJSON
      .filter((r) => r.edition === edition.id || edition.roles.includes(r.id))
      .sort((a, b) => b.team.localeCompare(a.team))
      .map((role) => [role.id, role]),
  );
};

const getTravelersNotInEdition = (edition = editionJSON[0]) => {
  return new Map(
    rolesJSON
      .filter(
        (r) =>
          r.team === "traveler" &&
          r.edition !== edition.id &&
          !edition.roles.includes(r.id),
      )
      .map((role) => [role.id, role]),
  );
};

const set =
  (key) =>
  ({ grimoire }, val) => {
    grimoire[key] = val;
  };

const toggle =
  (key) =>
  ({ grimoire }, val) => {
    if (val === true || val === false) {
      grimoire[key] = val;
    } else {
      grimoire[key] = !grimoire[key];
    }
  };

const clean = (id) => id.toLocaleLowerCase().replace(/[^a-z0-9]/g, "");

// global data maps
const editionJSONbyId = new Map(
  editionJSON.map((edition) => [edition.id, edition]),
);
const rolesJSONbyId = new Map(rolesJSON.map((role) => [role.id, role]));
const fabled = new Map(fabledJSON.map((role) => [role.id, role]));

const normalizeOfficialRoleKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const officialRolesByImageKey = rolesJSON.reduce((result, role) => {
  [role.id, role.name].forEach((value) => {
    const key = normalizeOfficialRoleKey(value);
    if (key && !result.has(key)) result.set(key, role);
  });
  return result;
}, new Map());

const officialRoleFromImage = (role) => {
  const image = String(
    (role && (role.image || role.iconUrl || role.icon || role.avatar)) || "",
  );
  if (!/clocktower-wiki\.gstonegames\.com/i.test(image)) return null;
  const matches = image.match(/\/([^/?#]+)\.png(?:[/?#]|$)/gi);
  if (!matches || !matches.length) return null;
  const key = normalizeOfficialRoleKey(
    matches[matches.length - 1].replace(/^\/|\.png.*$/gi, ""),
  );
  return officialRolesByImageKey.get(key) || null;
};

const officialNightDefaults = (role) => {
  const officialRole = officialRoleFromImage(role);
  if (!officialRole) return {};
  return {
    firstNight: officialRole.firstNight,
    firstNightReminder: officialRole.firstNightReminder,
    otherNight: officialRole.otherNight,
    otherNightReminder: officialRole.otherNightReminder,
    reminders: cloneJson(translateOfficialReminders(officialRole.reminders)),
    remindersGlobal: cloneJson(
      translateOfficialReminders(officialRole.remindersGlobal),
    ),
    setup: officialRole.setup,
  };
};

// jinxes
let jinxes = {};
try {
  // Note: can't fetch live list due to lack of CORS headers
  // fetch("https://bloodontheclocktower.com/script/data/hatred.json")
  //   .then(res => res.json())
  //   .then(jinxesJSON => {
  jinxes = new Map(
    jinxesJSON.map(({ id, hatred }) => [
      clean(id),
      new Map(hatred.map(({ id, reason }) => [clean(id), reason])),
    ]),
  );
  // });
} catch (e) {
  console.error("couldn't load jinxes", e);
}

// base definition for custom roles
const customRole = {
  id: "",
  name: "",
  image: "",
  ability: "",
  edition: "custom",
  firstNight: 0,
  firstNightReminder: "",
  otherNight: 0,
  otherNightReminder: "",
  reminders: [],
  remindersGlobal: [],
  setup: false,
  team: "townsfolk",
  isCustom: true,
  smallTokens: [],
  tokenImages: [],
  smallToken: "",
  tokenImage: "",
  tokenUrl: "",
  tokens: [],
};

const cloneJson = (value) =>
  value === undefined ? value : JSON.parse(JSON.stringify(value));

const hasExportValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === "object") return Object.keys(value).length > 0;
  return value !== undefined && value !== null && value !== "";
};

const buildCustomRoleState = (roles, roleLookup = new Map()) => {
  const processedRoles = roles
    // replace numerical role object keys with matching key names
    .map((role) => {
      if (role[0]) {
        const customKeys = Object.keys(customRole);
        const mappedRole = {};
        for (let prop in role) {
          if (customKeys[prop]) {
            mappedRole[customKeys[prop]] = role[prop];
          }
        }
        return mappedRole;
      } else {
        return role;
      }
    })
    // clean up role.id
    .map((role) => {
      role.id = clean(role.id);
      return role;
    })
    // map existing roles to base definition or pre-populate custom roles to ensure all properties
    .map((role) => {
      const officialRole = rolesJSONbyId.get(role.id);
      if (officialRole) return officialRole;

      return Object.assign(
        {},
        customRole,
        roleLookup.get(role.id) || {},
        officialNightDefaults(role),
        role,
      );
    })
    // default empty icons and placeholders, clean up firstNight / otherNight
    .map((role) => {
      if (rolesJSONbyId.get(role.id)) return role;
      role.imageAlt = // map team to generic icon
        {
          townsfolk: "good",
          outsider: "outsider",
          minion: "minion",
          demon: "evil",
          fabled: "fabled",
        }[role.team] || "custom";
      role.firstNight = Math.abs(role.firstNight);
      role.otherNight = Math.abs(role.otherNight);
      return role;
    })
    // filter out roles that don't match an existing role and also don't have name/ability/team
    .filter((role) => role.name && role.ability && role.team)
    // sort by team
    .sort((a, b) => b.team.localeCompare(a.team));

  const roleMap = new Map(
    processedRoles
      .filter((role) => role.team !== "fabled")
      .map((role) => [role.id, role]),
  );
  const fabledMap = new Map([
    ...processedRoles.filter((r) => r.team === "fabled").map((r) => [r.id, r]),
    ...fabledJSON.map((role) => [role.id, role]),
  ]);
  const otherTravelers = new Map(
    rolesJSON
      .filter(
        (r) =>
          r.team === "traveler" && !processedRoles.some((i) => i.id === r.id),
      )
      .map((role) => [role.id, role]),
  );

  return { roleMap, fabledMap, otherTravelers };
};

export default new Vuex.Store({
  modules: {
    players,
    session,
    storyLog,
    privateChat,
    room,
    voice,
    roleDraw,
  },
  state: {
    grimoire: {
      isNight: false,
      isNightOrder: true,
      isPublic: false,
      isMenuOpen: false,
      isStatic: false,
      isMuted: false,
      isImageOptIn: true,
      statusEffectsEnabled: true,
      roleDrawEnabled: false,
      zoom: 0,
      background: "",
    },
    modals: {
      announcement: false,
      edition: false,
      fabled: false,
      gameState: false,
      imageGenerator: false,
      gameRecord: false,
      login: false,
      myUploads: false,
      nightOrder: false,
      playerName: false,
      privateChat: false,
      reference: false,
      reminder: false,
      role: false,
      roleLibrary: false,
      roleTokenGenerator: false,
      roles: false,
      roomControl: false,
      roomLobby: false,
      scriptCreator: false,
      scriptLibrary: false,
      settings: false,
      storyLog: false,
      voteHistory: false,
    },
    edition: editionJSONbyId.get("tb"),
    roles: getRolesByEdition(),
    otherTravelers: getTravelersNotInEdition(),
    editionPickerTarget: "global",
    posterEdition: null,
    posterRoles: null,
    fabled,
    jinxes,
  },
  getters: {
    /**
     * Return all custom roles, with default values and non-essential data stripped.
     * Role object keys will be replaced with a numerical index to conserve bandwidth.
     * @param roles
     * @returns {[]}
     */
    customRolesStripped: ({ roles }) => {
      const customRoles = [];
      const customKeys = Object.keys(customRole);
      const strippedProps = ["isCustom"];
      roles.forEach((role) => {
        if (!role.isCustom) {
          customRoles.push({ id: role.id });
        } else {
          const strippedRole = {};
          for (let prop in role) {
            if (strippedProps.includes(prop)) {
              continue;
            }
            const value = role[prop];
            if (customKeys.includes(prop) && value !== customRole[prop]) {
              strippedRole[customKeys.indexOf(prop)] = value;
            }
          }
          customRoles.push(strippedRole);
        }
      });
      return customRoles;
    },
    customRolesFull: ({ roles }) => {
      const requiredProps = ["id", "name", "image", "ability", "team"];
      return Array.from(roles.values()).map((role) => {
        if (!role.isCustom) return role.id;
        const fullRole = {};
        Object.keys(customRole).forEach((prop) => {
          if (prop === "isCustom" || prop === "edition") return;
          const value = role[prop];
          const isRequired = requiredProps.includes(prop);
          const isDefault = value === customRole[prop];
          if (isRequired || (!isDefault && hasExportValue(value))) {
            fullRole[prop] = cloneJson(value);
          }
        });
        return fullRole;
      });
    },
    rolesJSONbyId: () => rolesJSONbyId,
  },
  mutations: {
    setZoom: set("zoom"),
    setBackground: set("background"),
    toggleMuted: toggle("isMuted"),
    toggleMenu: toggle("isMenuOpen"),
    toggleNightOrder: toggle("isNightOrder"),
    toggleStatic: toggle("isStatic"),
    toggleStatusEffects: toggle("statusEffectsEnabled"),
    toggleRoleDraw: toggle("roleDrawEnabled"),
    toggleNight: toggle("isNight"),
    toggleGrimoire: toggle("isPublic"),
    toggleImageOptIn: toggle("isImageOptIn"),
    toggleModal({ modals }, name) {
      if (name) {
        modals[name] = !modals[name];
      }
      for (let modal in modals) {
        if (modal === name) continue;
        modals[modal] = false;
      }
    },
    openModalOverlay({ modals }, name) {
      if (name) modals[name] = true;
    },
    closeModal({ modals }, name) {
      if (name) modals[name] = false;
    },
    /**
     * Store custom roles
     * @param state
     * @param roles Array of role IDs or full role definitions
     */
    setCustomRoles(state, roles) {
      const { roleMap, fabledMap, otherTravelers } = buildCustomRoleState(
        roles,
        state.roles,
      );
      state.roles = roleMap;
      // update Fabled to include custom Fabled from this script
      state.fabled = fabledMap;
      state.otherTravelers = otherTravelers;
    },
    setEditionPickerTarget(state, target) {
      state.editionPickerTarget = target === "poster" ? "poster" : "global";
    },
    setPosterScriptRoles(state, { roles, edition }) {
      const { roleMap } = buildCustomRoleState(roles);
      state.posterEdition = edition || { id: "custom" };
      state.posterRoles = roleMap;
      state.modals.edition = false;
      state.editionPickerTarget = "global";
    },
    setEdition(state, edition) {
      if (editionJSONbyId.has(edition.id)) {
        state.edition = editionJSONbyId.get(edition.id);
        state.roles = getRolesByEdition(state.edition);
        state.otherTravelers = getTravelersNotInEdition(state.edition);
      } else {
        state.edition = edition;
      }
      state.modals.edition = false;
      state.editionPickerTarget = "global";
    },
  },
  plugins: [persistence, socket, storyLogPlugin],
});
